import { locationUISchema } from "react-ui-components/components/location/locationUISchema"
import { DateTimeWidget, ImageWidget } from "../CustomWidgets"
import CustomObjectFieldTabs from "../raffle/CustomObjectFieldTabs"
import { normalizeAdForSave } from "./adFormUtils"

const renderImageThumbnail = (value?: string | null) =>
  !value ? null : <img src={value} alt="Ad" className="max-w-full h-auto rounded" style={{ maxWidth: 80 }} />

const renderLinkSummary = (value?: string | null) => {
  if (!value) return ""
  return value.length > 40 ? `${value.slice(0, 40)}…` : value
}

const renderDateTime = (value?: unknown) => {
  if (!value) return ""
  const date = typeof value === "number" ? new Date(value) : new Date(String(value))
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString()
}

const formatOverlayTextSummary = (value?: { text?: string } | null) => value?.text ?? ""

const formatLocationSummary = (value?: { city?: string; province?: string; country?: string } | null) => {
  if (!value) return ""
  return [value.city, value.province, value.country].filter(Boolean).join(", ")
}

/**
 * Configuration data for the global Ads component (top-level "ads" Firestore
 * collection, shown on the consumer app's Home screen). Distinct from
 * BusinessAdsConfig.tsx, which manages ads nested under a single business.
 */
export const configData = {
  getListLocation(id: any) {
    return ["ads"]
  },
  EDIT_TITLE: "Edit Ad",
  ADD_TITLE: "Add Ad",
  LOADING_MESSAGE: "Loading Ads...",
  TITLE: "Ads",
  IMAGES_BUCKET: "adsImages",

  ObjectFieldTemplate: CustomObjectFieldTabs,

  columnsProperties: [
    { hidden: true, name: "id", label: "Id" },
    { required: false, name: "disabled", default: false, label: "Disabled", type: "boolean" },

    { required: true, name: "name", label: "Name" },
    {
      required: true,
      name: "img",
      label: "Image",
      options: { customBodyRender: renderImageThumbnail },
    },
    {
      required: false,
      name: "link",
      label: "Link (URL)",
      type: "string",
      options: { customBodyRender: renderLinkSummary },
    },
    {
      required: false,
      type: "integer",
      name: "expirationDate",
      label: "Expiration Date",
      options: {
        customBodyRender: renderDateTime,
        setCellProps: () => ({ style: { maxWidth: 180 } }),
      },
    },
    {
      required: false,
      name: "overlayText",
      label: "Overlay Text",
      default: { text: "", style: "" },
      type: "object",
      properties: {
        text: { type: "string", title: "Text" },
        style: { type: "string", title: "Style (JS object literal)" },
      },
      options: { customBodyRender: formatOverlayTextSummary },
    },
    {
      required: false,
      name: "location",
      label: "Location",
      default: { country: "*", province: "*", city: "*" },
      type: "object",
      properties: {
        country: { type: "string", title: "Country" },
        province: { type: "string", title: "State" },
        city: { type: "string", title: "City" },
      },
      options: { customBodyRender: formatLocationSummary },
    },
  ],

  uiSchema: {
    img: {
      "ui:widget": ImageWidget,
    },
    disabled: {
      "ui:widget": "radio",
    },
    expirationDate: {
      "ui:widget": "datetime",
    },
    location: {
      ...locationUISchema,
    },
  },

  widgets: { datetime: DateTimeWidget },

  async imageProcessor(formData: { img: any }, handleUpload: (arg0: any, arg1: string, arg2: any) => any, id: any) {
    if (formData.img) {
      const img = await handleUpload(id, this.IMAGES_BUCKET, formData.img)
      formData.img = img
    }
    return normalizeAdForSave(formData)
  },
}
